import type { AnalyticsOverview, DashboardReview, ReviewStats, ReviewStatus } from '../types/dashboard'

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'

async function handleResponse<T>(res: Response): Promise<T> {
  if (res.ok) return res.json() as Promise<T>

  let detail: string | undefined
  try {
    const body = await res.json()
    detail = body?.detail
  } catch {
    // ignore parse errors
  }

  if (res.status === 401) throw new Error('unauthorized')
  if (res.status === 429) throw new Error('rate_limit_exceeded')
  throw new Error(detail ?? `Request failed with status ${res.status}`)
}

export async function getReviews(
  apiKey: string,
  locationId?: string,
  status?: ReviewStatus,
  limit = 100,
  offset = 0,
): Promise<DashboardReview[]> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) })
  if (locationId) params.set('location_id', locationId)
  if (status) params.set('status', status)

  const res = await fetch(`${BASE_URL}/api/reviews/?${params}`, {
    headers: { 'X-API-Key': apiKey },
  })
  return handleResponse<DashboardReview[]>(res)
}

export async function getStats(apiKey: string, locationId?: string): Promise<ReviewStats> {
  const params = new URLSearchParams()
  if (locationId) params.set('location_id', locationId)

  const res = await fetch(`${BASE_URL}/api/reviews/stats/summary?${params}`, {
    headers: { 'X-API-Key': apiKey },
  })
  return handleResponse<ReviewStats>(res)
}

/**
 * Trigger an immediate fetch + AI-processing pass for one location — used by
 * the Settings page's "Get My Reviews" button (typically clicked right after
 * connecting Google Business Profile, so the owner doesn't have to wait for
 * the next automatic poll cycle). Returns the number of reviews fetched.
 */
export async function processLocationReviews(apiKey: string, locationId: string): Promise<number> {
  const params = new URLSearchParams({ location_id: locationId })
  const res = await fetch(`${BASE_URL}/api/reviews/process?${params}`, {
    method: 'POST',
    headers: { 'X-API-Key': apiKey },
  })
  const body = await handleResponse<{ reviews_fetched?: number }>(res)
  return body.reviews_fetched ?? 0
}

export async function getAnalyticsOverview(
  apiKey: string,
  locationId?: string,
  days = 30,
): Promise<AnalyticsOverview> {
  const params = new URLSearchParams({ days: String(days) })
  if (locationId) params.set('location_id', locationId)

  const res = await fetch(`${BASE_URL}/api/analytics/overview?${params}`, {
    headers: { 'X-API-Key': apiKey },
  })
  return handleResponse<AnalyticsOverview>(res)
}

export async function approveReview(
  jwt: string,
  reviewId: string,
  responseText: string,
): Promise<void> {
  const res = await fetch(`${BASE_URL}/api/reviews/${encodeURIComponent(reviewId)}/approve`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwt}`,
    },
    body: JSON.stringify({ response_text: responseText }),
  })
  await handleResponse<unknown>(res)
}

export async function rejectReview(jwt: string, reviewId: string): Promise<void> {
  const res = await fetch(`${BASE_URL}/api/reviews/${encodeURIComponent(reviewId)}/reject`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${jwt}` },
  })
  await handleResponse<unknown>(res)
}

export interface BulkGenerateResult {
  results: Array<{ success: boolean; review_id: string; status?: string; escalated?: boolean; error?: string }>
  errors: string[]
}

/**
 * Regenerate AI responses for multiple reviews in one call — used by the
 * dashboard's "select reviews → generate" bulk action. Unlike the automatic
 * poll cycle, this never sends an approval email; reviews are left in
 * pending_approval/escalated status for in-app Post/Edit/Reject.
 */
export async function bulkGenerateReviews(
  jwt: string,
  reviewIds: string[],
): Promise<BulkGenerateResult> {
  const res = await fetch(`${BASE_URL}/api/reviews/bulk-generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwt}`,
    },
    body: JSON.stringify({ review_ids: reviewIds }),
  })
  return handleResponse<BulkGenerateResult>(res)
}

/**
 * Fetch one real Google review for a location via SerpAPI, run it through
 * the AI pipeline, and return the saved review. Requires SERPAPI_KEY on the
 * server. Throws descriptive errors on failure.
 */
export async function fetchLiveReview(
  jwt: string,
  locationId: string,
): Promise<DashboardReview> {
  const res = await fetch(`${BASE_URL}/api/reviews/fetch-live-review`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwt}`,
    },
    body: JSON.stringify({ location_id: locationId }),
  })
  return handleResponse<DashboardReview>(res)
}
